// --- Residence assets ---
import twilightDay from "../assets/portfolio/twilight-day.jpg";
import twilightNight from "../assets/portfolio/twilight-night.jpg";
import linerHorizon from "../assets/portfolio/liner-horizon.jpg";
import cornerVilla from "../assets/portfolio/corner-villa.jpg";
import cornerVilla3D from "../assets/portfolio/corner-villa-3d.jpg";
import cornerVillaOnsite from "../assets/portfolio/corner-villa-onsite.jpg";

// --- Interior assets ---
import royalKitchen from "../assets/portfolio/royal-kitchen.jpg";
import warmLiving from "../assets/portfolio/warm-living.jpg";
import flutedContrast from "../assets/portfolio/fluted-contrast-living.jpg";
import classicalMaster from "../assets/portfolio/classical-master.jpg";
import counterKitchen3D from "../assets/portfolio/counter-kitchen-3d.jpg";
import counterKitchenOnsite from "../assets/portfolio/counter-kitchen-onsite.jpg";
import flutedTV3D from "../assets/portfolio/fluted-tv-3d.jpg";
import flutedTVProgress from "../assets/portfolio/fluted-tv-progress.jpg";

// --- Commercial / concept ---
import officeDay from "../assets/portfolio/office-day.jpg";
import officeNight from "../assets/portfolio/office-night.jpg";
import officeInt1 from "../assets/portfolio/office-interior-1.jpg";
import officeInt2 from "../assets/portfolio/office-interior-2.jpg";
import officeInt3 from "../assets/portfolio/office-interior-3.jpg";
import astro1 from "../assets/portfolio/astronomy-1.jpg";
import astro2 from "../assets/portfolio/astronomy-2.jpg";
import astro3 from "../assets/portfolio/astronomy-3.jpg";
import astro4 from "../assets/portfolio/astronomy-4.jpg";
import astro5 from "../assets/portfolio/astronomy-5.jpg";
import school1 from "../assets/portfolio/school-1.jpg";
import school2 from "../assets/portfolio/school-2.jpg";
import school3 from "../assets/portfolio/school-3.jpg";
import school4 from "../assets/portfolio/school-4.jpg";

export const CATEGORIES = [
  { key: "all", label: "All Works" },
  { key: "residence", label: "Residences" },
  { key: "interior", label: "Luxury Interiors" },
  { key: "commercial", label: "Commercial" },
  { key: "concept", label: "Concept Designs" },
];

// Helper to collect all images of a project (used by lightbox + detail)
const imgs = (arr) => arr.filter(Boolean);

export const PROJECTS = [
  {
    id: "twilight",
    title: "Twilight Residence",
    place: "Taloja",
    category: "residence",
    type: "slider",
    before: twilightDay,
    after: twilightNight,
    beforeLabel: "Day",
    afterLabel: "Night",
    caption: "A single facade — reborn from dawn light to lantern glow.",
    brief:
      "A residence on a tight urban plot in Taloja, planned around a private central stair and screened by a wooden pergola. The design pivots between two lives — a soft, ventilated day facade and a warm, lit-from-within night composition. Every profile lighting fixture was selected to reveal the stone and teak textures at dusk.",
    materials: [
      { color: "#2a1e35", label: "Char Grey" },
      { color: "#b48e5b", label: "Warm Teak" },
      { color: "#e9d5b7", label: "Cane Beige" },
      { color: "#ff9d5c", label: "Amber Glow" },
    ],
    images: imgs([twilightDay, twilightNight]),
  },
  {
    id: "liner-horizon",
    title: "Liner Horizon Villa",
    place: "Palaspa",
    category: "residence",
    type: "single",
    src: linerHorizon,
    caption: "A vertical residence sculpted around light, air, and privacy.",
    brief:
      "A slim three-storey villa in Palaspa arranged as a vertical stack of balconies and screens. Load-bearing walls are pushed inboard so every level opens fully to the street with floor-to-ceiling glazing. The chalk-white plaster contrasts a monolithic grey plinth that anchors it to the ground.",
    materials: [
      { color: "#f4f1f5", label: "Chalk White" },
      { color: "#6d6564", label: "Warm Grey" },
      { color: "#2b2b2b", label: "Slate" },
      { color: "#c5b299", label: "Sand Stone" },
    ],
    images: imgs([linerHorizon]),
  },
  {
    id: "corner-villa",
    title: "The Corner Villa",
    place: "Panvel",
    category: "residence",
    type: "slider",
    hero: cornerVilla,
    before: cornerVilla3D,
    after: cornerVillaOnsite,
    beforeLabel: "3D Model",
    afterLabel: "On Site",
    caption: "From the sketchup massing to the raw concrete frame — as-built.",
    brief:
      "A corner-plot villa in Panvel massed as two overlapping volumes — a warm ivory stone box embracing a taller walnut-clad tower. The sketchup study set proportions early; the on-site frame preserved the same shadow lines through disciplined formwork.",
    materials: [
      { color: "#d6c9b6", label: "Ivory Stone" },
      { color: "#6f5a48", label: "Walnut" },
      { color: "#8b8b8b", label: "Cool Grey" },
      { color: "#1a1518", label: "Onyx" },
    ],
    images: imgs([cornerVilla, cornerVilla3D, cornerVillaOnsite]),
  },
  {
    id: "royal-kitchen",
    title: "The Royal Kitchen",
    place: "Panvel",
    category: "interior",
    type: "single",
    src: royalKitchen,
    caption: "Classical proportions, gilded mouldings, and warm cove lighting.",
    brief:
      "A ceremonial kitchen with hand-detailed panelling and burnished brass ironmongery in Panvel. Cream marble runs continuously across countertops and the rear wall, framed by rich oak millwork and warm cove lighting.",
    materials: [
      { color: "#ecdcc4", label: "Cream Marble" },
      { color: "#a67b3b", label: "Burnished Brass" },
      { color: "#4b3a2c", label: "Rich Oak" },
      { color: "#7d3232", label: "Bordeaux" },
    ],
    images: imgs([royalKitchen]),
  },
  {
    id: "warm-living",
    title: "Warm Living",
    place: "Panvel",
    category: "interior",
    type: "single",
    src: warmLiving,
    caption: "Layered warm whites, textured cladding, and amber LED profiles.",
    brief:
      "A layered living room in Panvel built around textured warm whites and amber LED profile lighting. The palette moves from latte at the floor to espresso at the ceiling coffers, punctuated by copper accents.",
    materials: [
      { color: "#e8ddc9", label: "Latte" },
      { color: "#b48e5b", label: "Warm Teak" },
      { color: "#4d3b2a", label: "Espresso" },
      { color: "#d99460", label: "Copper Glow" },
    ],
    images: imgs([warmLiving]),
  },
  {
    id: "fluted-contrast",
    title: "Fluted Contrast Living",
    place: "Panvel",
    category: "interior",
    type: "single",
    src: flutedContrast,
    caption: "Vertical fluted teak against Statuario — quiet contrast, loud detail.",
    brief:
      "A double-height living space in Panvel where vertical fluted teak meets Statuario marble on the TV wall. Cool slate and ash tones on the sofa and floor keep the composition grounded while the shadow lines of the flutes catch the daylight.",
    materials: [
      { color: "#ececec", label: "Statuario" },
      { color: "#6a4a2a", label: "Fluted Teak" },
      { color: "#4a4a4a", label: "Slate Charcoal" },
      { color: "#9a9a9a", label: "Cool Ash" },
    ],
    images: imgs([flutedContrast]),
  },
  {
    id: "classical-master",
    title: "Classical Master Suite",
    place: "Panvel",
    category: "interior",
    type: "single",
    src: classicalMaster,
    caption: "A retreat wrapped in tonal linens and hand-detailed wall panelling.",
    brief:
      "A serene master bedroom retreat wrapped in tonal linens and hand-detailed classical wall panelling. Warm cove lighting bounces off the champagne ceiling, while the bespoke bed niche floats in soft sand-linen upholstery.",
    materials: [
      { color: "#e6ddc7", label: "Champagne" },
      { color: "#b39775", label: "Sand Linen" },
      { color: "#4d3f30", label: "Espresso" },
      { color: "#d4c0a2", label: "Bisque" },
    ],
    images: imgs([classicalMaster]),
  },
  {
    id: "counter-kitchen",
    title: "The Counter Kitchen",
    place: "Panvel",
    category: "interior",
    type: "slider",
    before: counterKitchen3D,
    after: counterKitchenOnsite,
    beforeLabel: "3D Model",
    afterLabel: "On Site",
    caption: "The render on the left. The finished countertop on the right.",
    brief:
      "A modern L-shape kitchen in Panvel with mint-washed cabinetry and warm oak upper storage. The render defined every joinery reveal; the on-site countertop follows the drawing to the millimetre.",
    materials: [
      { color: "#e8f0e1", label: "Mint Wash" },
      { color: "#d6c297", label: "Oak" },
      { color: "#f2ede4", label: "Ivory" },
      { color: "#3a3a3a", label: "Slate" },
    ],
    images: imgs([counterKitchen3D, counterKitchenOnsite]),
  },
  {
    id: "fluted-tv",
    title: "Fluted TV Wall",
    place: "Palaspa",
    category: "interior",
    type: "slider",
    before: flutedTV3D,
    after: flutedTVProgress,
    beforeLabel: "3D Model",
    afterLabel: "Progress",
    caption: "Design intent, brought to precise on-site execution.",
    brief:
      "A media wall design in Palaspa combining vertical fluted walnut with concealed amber LED strips. The render describes the mood; the site image captures the moment the joinery lands into its designed pocket.",
    materials: [
      { color: "#7a563a", label: "Fluted Walnut" },
      { color: "#f0e6d6", label: "Off White" },
      { color: "#b98b52", label: "Amber LED" },
      { color: "#2a2a2a", label: "Charcoal" },
    ],
    images: imgs([flutedTV3D, flutedTVProgress]),
  },
  {
    id: "office-building",
    title: "Office Building",
    place: "Ulwe",
    category: "commercial",
    type: "mixed",
    slider: {
      before: officeDay,
      after: officeNight,
      beforeLabel: "Day",
      afterLabel: "Night",
    },
    interiors: [officeInt1, officeInt2, officeInt3],
    caption: "Facade by day, monolith by night — with interiors engineered for focus.",
    brief:
      "A five-storey commercial office in Ulwe with a bronze fin-line facade and a triple-height entry canopy. The interiors are engineered for focus — from executive cabins with framed art to boardrooms opening to a curated city view.",
    materials: [
      { color: "#24344a", label: "Twilight Blue" },
      { color: "#b8845a", label: "Bronze Fin" },
      { color: "#d6d0c4", label: "Warm White" },
      { color: "#0f0f0f", label: "Curtain Wall" },
    ],
    images: imgs([officeDay, officeNight, officeInt1, officeInt2, officeInt3]),
  },
  {
    id: "astronomy-museum",
    title: "Astronomy Museum",
    place: "Dholera, Gujarat",
    category: "concept",
    tag: "Concept Design",
    type: "gallery",
    images: imgs([astro1, astro2, astro3, astro4, astro5]),
    caption: "A stargazing pavilion — geometry as an instrument of wonder.",
    brief:
      "A concept design for a public astronomy pavilion in Dholera, Gujarat. Cosmos-black facades open at the roof to a shaped skylight above the primary gallery, wrapping the visitor in a controlled experience of darkness, light, and orbit.",
    materials: [
      { color: "#0f0914", label: "Cosmos Black" },
      { color: "#4a1c40", label: "Nebula Plum" },
      { color: "#c85a54", label: "Solar Coral" },
      { color: "#e6e6e6", label: "Aluminium" },
    ],
  },
  {
    id: "school",
    title: "Pushpak Nagar School",
    place: "Navi Mumbai",
    category: "concept",
    tag: "Concept Design",
    type: "gallery",
    images: imgs([school1, school2, school3, school4]),
    caption: "Chromatic learning blocks arranged around a shaded central court.",
    brief:
      "A concept design for a chromatic K-12 school in Navi Mumbai. Learning blocks arranged around a shaded central court, each block coded in a distinct primary hue to help wayfinding for younger students.",
    materials: [
      { color: "#d94b3e", label: "Vermilion" },
      { color: "#f2c74a", label: "Sunburst" },
      { color: "#4a8fc0", label: "Sky Blue" },
      { color: "#f4f1f5", label: "Chalk" },
    ],
  },
];
